import db from './db.js';
import api from './api.js';
import router from './router.js';
import formset from './formset.js';
import record, { flatten, deepen } from './record.js';
import filter, { translate, resolveFilterLabelsToIds } from './filter.js';
import setup from './setup.js';
import grid from './grid.js';
import { notify, ask } from './msgbox.js';
import { removeEvents, findByAttr } from './misc.js';
import crud_view from './views/crud.js';
import import_form from './forms/import.js';

const crud = {

	module_name: null,
	target_node: null,
	fieldspecs: null,		// [{ index: 1, field: 'phone', label: 'Telefone', types: [types.phone()], placeholder: '+00 (00) 00000-0000', title: 'Telefone com DDI e DDD' }, ...]
	record_form: null,
	record_count: 0,		// Record count
	page_count: 0,			// Page count
	page_number: 1,			// Current page
	page_length: 30,		// Lines per page
	sort_column: 1,			// Sorting column index (1-based)
	sort_order: 1,			// 0=Asc, 1=Desc (padrão: mais recente primeiro)
	filter: {},				// Filter expression
	filterExplicitlyApplied: false,

	create(properties)
	{
		return Object.assign(Object.create(this), properties);
	},

	async run(module_name, label, target_node, grid_view, record_form, fieldspecs, operations, reports, options = {})
	{
		try
		{
			if (options.default_columns)
				this.default_columns = options.default_columns;
			this.fieldspecs = fieldspecs;
			this.record_form = record_form;
			this.module_name = module_name;
			this.target_node = removeEvents(target_node);
			this.target_node.innerHTML = crud_view(label, operations, reports);
			filter.init(fieldspecs);
			setup.init(fieldspecs);
			grid.init(module_name + '.grid', grid_view, this.target_node.querySelector('#grid'));
			this.target_node.addEventListener('click', this.handleEvents.bind(this));
			this.applySetup(this.loadSetup());
			this.setMenuRoutes();
			await this.loadBaseData();
			// Resolver labels→IDs após loadBaseData (db.list('companies') precisa estar populado)
			if (this.filter && Object.keys(this.filter).some(k => /^field-\d+$/.test(k))) {
				this.filter = await resolveFilterLabelsToIds(this.filter, this.fieldspecs);
			}
			if (!options.skipInitialLoad)
				await this.loadPage();
		}
		catch (e)
		{
			notify(e.message, 'Falha');
			console.log(e);
		}
	},

	handleEvents(ev)
	{
		let submenu, header, record;
		if (submenu = findByAttr(ev.target, 'hash'))
			router.route(submenu.getAttribute('hash'));
		else if (header = findByAttr(ev.target, 'data-index'))
			this.sortPage(header.getAttribute('data-index'))
		else if (record = findByAttr(ev.target, 'data-id'))
		{
			this.loadRecord(record.getAttribute('data-id'))
				.then((data) => { if (data != null) this.editRecord.call(this, data); })
				.catch((e) => notify(e?.message || String(e), 'Atenção'));
		}
	},

	setMenuRoutes()
	{
		router.set('#insert', this.insertRecord.bind(this));
		router.set('#filter', this.editFilter.bind(this));
		router.set('#setup', this.editSetup.bind(this));
		router.set('#export', this.exportFileDialog.bind(this));
		router.set('#import', this.importFileDialog.bind(this));
		router.set('#first', this.goToFirstPage.bind(this));
		router.set('#previous', this.goToPrevPage.bind(this));
		router.set('#reload', this.reloadPage.bind(this));
		router.set('#next', this.goToNextPage.bind(this));
		router.set('#last', this.goToLastPage.bind(this));
	},

	async addFilter(params)
	{
		let f = this.filter;
		if (f && Object.keys(f).some(k => /^field-\d+$/.test(k)))
			f = await resolveFilterLabelsToIds(f, this.fieldspecs);
		const filter = translate(f, null, this.fieldspecs);
		if (filter)
			params.append('filter', filter);
	},

	async addFilterForLocations(params)
	{
		let f = this.filter;
		if (f && Object.keys(f).some(k => /^field-\d+$/.test(k)))
			f = await resolveFilterLabelsToIds(f, this.fieldspecs);
		// Só escopo (empresa/armazém) — nunca critérios de posição (location_address quebra GET /locations).
		const expr = translate(f, ['company_id', 'warehouse_id'], this.fieldspecs);
		if (expr)
			params.append('filter', expr);
	},

	addSort(params)
	{
		params.append('sort', this.sort_column * (this.sort_order ? -1 : 1));
	},

	computeRecencySort()
	{
		const fs = this.fieldspecs || [];
		const pick = (name) => fs.find(s => s.field === name)?.index;
		const col = pick('created_on') ?? pick('updated_on') ?? pick('id') ?? pick('location_id') ?? (fs[0]?.index ?? 1);
		return { sort_column: col, sort_order: 1 };
	},

	addLimit(params, limit)
	{
		params.append('limit', limit ?? this.page_length);
	},

	async addListLimit(params)
	{
		// Sempre paginar: limit 0 omite LIMIT no backend e pode travar a API / o browser.
		params.append('limit', this.page_length);
	},

	addOffset(params)
	{
		params.append('offset', (this.page_number - 1) * this.page_length);
	},

	addCount(params)
	{
		params.append('group', 'count');
	},

	queryAll()
	{
		let params = new URLSearchParams();
		this.addLimit(params, 0);
		return '?' + params.toString();
	},

	async queryPage()
	{
		let params = new URLSearchParams();
		await this.addFilter(params);
		this.addSort(params);
		await this.addListLimit(params);
		this.addOffset(params);
		return '?' + params.toString();
	},

	async queryFiltered()
	{
		let params = new URLSearchParams();
		await this.addFilter(params);
		this.addSort(params);
		this.addLimit(params, 0);
		return '?' + params.toString();
	},

	async queryFilteredForLocations()
	{
		let params = new URLSearchParams();
		await this.addFilterForLocations(params);
		params.append('sort', '4');
		params.append('limit', '0');
		return '?' + params.toString();
	},

	/**
	 * Query de posições somente com filtro company/warehouse no filtro atual.
	 * Retorna null sem escopo — evita GET /locations?limit=0 (~4MB+).
	 */
	async queryScopedLocationsOrNull(filterOverride = null)
	{
		const params = new URLSearchParams();
		const saved = this.filter;
		if (filterOverride != null)
			this.filter = filterOverride;
		try
		{
			await this.addFilterForLocations(params);
		}
		finally
		{
			if (filterOverride != null)
				this.filter = saved;
		}
		if (!params.has('filter'))
			return null;
		params.append('sort', '4');
		params.append('limit', '0');
		return '?' + params.toString();
	},

	/** Grava posições no IndexedDB apenas quando há escopo no filtro. */
	async loadScopedLocationsToDb(filterOverride = null)
	{
		const q = await this.queryScopedLocationsOrNull(filterOverride);
		if (!q)
			return;
		const data = await api.call(api.list_locations, q);
		await db.set('locations', Array.isArray(data) ? data : []);
	},

	/** Catálogos leves (empresa, armazém, dispositivo) — seguro na abertura da tela. */
	loadLightBaseCatalogs()
	{
		const q = this.queryAll();
		return Promise.all([
			api.call(api.list_companies, q).then((data) => db.set('companies', data)).catch(error => notify(error)),
			api.call(api.list_warehouses, q).then((data) => db.set('warehouses', data)).catch(error => notify(error)),
			api.call(api.list_devices, q).then((data) => db.set('devices', data)).catch(() => {}),
		]);
	},

	async queryCount()
	{
		let params = new URLSearchParams();
		await this.addFilter(params);
		this.addLimit(params, 0);
		this.addCount(params);
		return '?' + params.toString();
	},

	async loadBaseData()	// Overload
	{
		api.call(api.list_something, this.queryAll()).then((data) => db.set('something', data).catch(error => notify(error)));
	},

	async queryData(query)	// Overload
	{
		return await api.call(api.list_records, query);
	},

	async queryFilteredData()
	{
		return await this.queryData(await this.queryFiltered());
	},

	async loadPage(page)
	{
		if (page)
			this.page_number = Number(page);
		// Garantir que labels (ex: nick da empresa) sejam convertidos em IDs antes da API
		if (this.filter && Object.keys(this.filter).some(k => /^field-\d+$/.test(k))) {
			this.filter = await resolveFilterLabelsToIds(this.filter, this.fieldspecs);
		}
		const count = await this.queryData(await this.queryCount());
		this.record_count = typeof count === 'number' ? count : 0;
		this.page_count = Math.max(0, Math.ceil(this.record_count / this.page_length));
		if (this.page_count > 0 && this.page_number > this.page_count)
			this.page_number = this.page_count;
		document.getElementById('page').innerText = this.record_count === 0
			? '0/0'
			: `${this.page_number}/${this.page_count}`;
		const records = await this.queryData(await this.queryPage());
		await grid.render(Array.isArray(records) ? records : []);
	},

	sortPage(column)
	{
		if (this.sort_column == column)
		{
			this.sort_order = this.sort_order ^ 1;
		}
		else
		{
			this.sort_column = column;
			this.sort_order = 1;
		}
		this.loadPage();
	},

	insertRecord()		// Overload
	{
		this.editRecord();
	},

	async loadRecord(id)	// Overload
	{
		let _record = await api.call(api.select_record, ...JSON.parse(id));
		return flatten(_record, 'items');
	},

	editRecord(data)
	{
		record.edit(this.record_form, this.saveRecordWrapper.bind(this), data);
	},

	async saveRecordWrapper(data, old_data)
	{
		let success = await this.saveRecord(data, old_data);
		this.loadPage();
		return success;
	},

	async saveRecord(data, old_data)	// Overload
	{
		data = deepen(data, 'items');
		if (old_data)
			return await api.call(api.update_record, data.company_id, data.id, _record);
		else
			return await api.call(api.insert_record, data.company_id, _record);
	},

	editFilter()
	{
		filter.edit(this.applyFilter.bind(this), this.filter);
	},

	async applyFilter(data)
	{
		this.filter = data;
		this.filterExplicitlyApplied = true;
		await this.loadPage(1);
	},

	loadSetup()
	{
		return JSON.parse(localStorage.getItem(this.module_name + '.setup') || '{}');
	},

	editSetup()
	{
		setup.edit(this.saveSetup.bind(this), this.loadSetup());
	},

	saveSetup(data)
	{
		if (data.keep_filter)
			data.filter = this.filter;
		localStorage.setItem(this.module_name + '.setup', JSON.stringify(data));
		this.applySetup(data);
		this.loadPage();
	},

	applySetup(data)
	{
		const rec = this.computeRecencySort();
		this.sort_column = rec.sort_column;
		this.sort_order = 1;
		this.page_length = Number(data.page_length ?? this.page_length);
		this.filter = data.filter ?? this.filter;
		let _default = this.default_columns
			|| '1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23,24,25';
		let columns = (data.columns || _default).split(',').map(column => Number(column));
		grid.setVisible(this.fieldspecs.map(field => columns.includes(field.index) ? 1 : 0));
	},

	async exportFile()
	{
		let ids = await this.getSelectedIds(['company_id', 'id']);
		return await api.call(api.export_endpoint);
	},

	exportFileDialog()
	{
		ask(`Exportar ${this.getSelection().length || this.record_count} registros?`, 'Exportação', async (answer) =>
		{
			if (answer == 'Y')
			{
				let data = await this.exportFile();
				this.clearGridSelection();
				notify(`<a href="data:application/vnd.ms-excel;base64,${encodeURI(data.file)}" download="export.xlsx">Clique aqui para obter o arquivo</a>`, 'Download');
			}
		});
	},

	async importFile(data)
	{
		return await api.call(api.import_endpoint, { file: data.file });
	},

	importFileDialog()
	{
		let fs = formset.create(import_form, async (data) =>
		{
			let response = await this.importFile(data);
			if (response?.text)
				notify(response.text);
			fs.destroy();
		});
		fs.init = (() =>
		{
			Object.getPrototypeOf(fs).init.call(fs);
			fs.form.eraseValues();
			fs.form.refresh();
		})();
	},

	goToPage(page)
	{
		if (page > 0 && page <= this.page_count)
			this.loadPage(page);
	},

	goToFirstPage()
	{
		if (this.page_number != 1)
			this.goToPage(1);
	},

	goToPrevPage()
	{
		this.goToPage(this.page_number - 1);
	},

	reloadPage()
	{
		this.goToPage(this.page_number);
	},

	goToNextPage()
	{
		this.goToPage(this.page_number + 1);
	},

	goToLastPage()
	{
		if (this.page_number != this.page_count)
			this.goToPage(this.page_count);
	},

	getSelection()
	{
		return grid.getSelection().map(entry => JSON.parse(entry));
	},

	clearGridSelection()
	{
		grid.clearSelection();
	},

	getCount()
	{
		return this.getSelection().length || this.record_count;
	},

	getListOf(array, properties)
	{
		return array.map(entry => Array.isArray(properties) ? properties.map(property => entry[property]) : entry[properties]);
	},

	async getSelectedIds(properties)
	{
		const normalize = (entry) =>
		{
			if (entry == null)
				return null;
			if (typeof entry === 'string')
			{
				try
				{
					const parsed = JSON.parse(entry);
					if (Array.isArray(parsed) && Array.isArray(properties) && parsed.length >= properties.length)
						return properties.map((_, i) => parsed[i]);
					if (parsed && typeof parsed === 'object' && !Array.isArray(parsed) && properties)
						return properties.map((p) => parsed[p]);
				}
				catch (_)
				{
					return null;
				}
				return null;
			}
			if (Array.isArray(entry) && Array.isArray(properties) && entry.length >= properties.length)
				return properties.map((_, i) => entry[i]);
			if (entry && typeof entry === 'object' && !Array.isArray(entry) && properties)
				return properties.map((p) => entry[p]);
			return null;
		};

		let ids = this.getSelection();
		if (!ids.length)
			return this.getListOf(await this.queryFilteredData(), properties);
		return ids.map(normalize).filter((row) => row != null);
	},

};

export default crud;

